import {
  ConnectRequired,
  DialogForm,
  Input,
  Label,
} from "@gearbox-protocol/ui-kit";
import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { Hex } from "viem";
import { useBytecodeUpload } from "@/hooks/use-bytecode-upload";

interface UploadBytecodeDialogProps {
  onClose: () => void;
}

export function UploadBytecodeDialog({ onClose }: UploadBytecodeDialogProps) {
  const navigate = useNavigate();
  const [jsonFile, setJsonFile] = useState<File | null>(null);
  const [contractType, setContractType] = useState("");
  const [version, setVersion] = useState("");
  const [sourceUrl, setSourceUrl] = useState("");
  const [errors, setErrors] = useState({
    version: "",
    sourceUrl: "",
  });

  const onSuccess = (bytecodeHash: Hex) => {
    // Reset form
    setJsonFile(null);
    setContractType("");
    setVersion("");
    setSourceUrl("");
    onClose();
    navigate(`/hash/${bytecodeHash}`);
  };

  const { uploadBytecode, isUploading, error, isContractsReady } = useBytecodeUpload({
    onSuccess,
  });

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.files?.[0]) {
      setJsonFile(e.target.files[0]);
    }
  };

  const validateForm = () => {
    const newErrors = {
      version: "",
      sourceUrl: "",
    };

    // Validate version is a positive number
    const versionNum = Number(version);
    if (Number.isNaN(versionNum) || versionNum <= 0) {
      newErrors.version = "Version must be a positive number";
    }

    // Validate sourceUrl is a valid URL
    try {
      new URL(sourceUrl);
    } catch {
      newErrors.sourceUrl = "Please enter a valid URL";
    }

    setErrors(newErrors);
    return !newErrors.version && !newErrors.sourceUrl;
  };

  const handleSubmit = (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    if (!validateForm()) return;

    uploadBytecode({
      contractType,
      version: Number(version),
      sourceUrl,
      jsonFile,
    });
  };

  return (
    <DialogForm
      onCancel={onClose}
      title="Upload Bytecode JSON"
      saveButtonText={
        isUploading ? "Uploading..." : !isContractsReady ? "Loading..." : "Submit"
      }
      isFormValid={
        !!contractType &&
        !!version &&
        !!sourceUrl &&
        !isUploading &&
        isContractsReady
      }
      onSubmit={handleSubmit}
      isLoading={isUploading}
      submitButtonVariant="pink"
      authWrapper={ConnectRequired}
    >
      <div className="space-y-4">
        <div className="space-y-2">
          <Label htmlFor="contractType">Contract Type</Label>
          <Input
            id="contractType"
            value={contractType}
            onChange={e => setContractType(e.target.value)}
            placeholder="IRM_DEFAULT"
            disabled={isUploading}
          />
        </div>
        <div className="space-y-2">
          <Label htmlFor="version">Version</Label>
          <Input
            id="version"
            type="number"
            min="0"
            step="any"
            value={version}
            onChange={e => setVersion(e.target.value)}
            placeholder="e.g., 1.0"
            disabled={isUploading}
          />
          {errors.version && (
            <p className="text-sm text-destructive mt-1 max-w-full break-words">
              {errors.version}
            </p>
          )}
        </div>
        <div className="space-y-2">
          <Label htmlFor="sourceUrl">Source URL</Label>
          <Input
            id="sourceUrl"
            value={sourceUrl}
            onChange={e => setSourceUrl(e.target.value)}
            placeholder="https://github.com/your-repo/..."
            disabled={isUploading}
          />
          {errors.sourceUrl && (
            <p className="text-sm text-destructive mt-1 max-w-full break-words">
              {errors.sourceUrl}
            </p>
          )}
        </div>
        <div className="space-y-2">
          <Label htmlFor="jsonFile">Select JSON file from Forge</Label>
          <Input
            id="jsonFile"
            type="file"
            onChange={handleFileChange}
            disabled={isUploading}
          />
        </div>
        {error && (
          <p className="text-sm text-destructive max-w-full break-words">
            {error.message}
          </p>
        )}
      </div>
    </DialogForm>
  );
}
